import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/features/auth/auth-store';
import {
  type CreateInviteResult,
  type InviteSummary,
  type MemberSummary,
  createInvite,
  listInvites,
  listMembers,
  removeMember,
  revokeInvite,
  updateMemberRole,
} from '@/features/org/members-api';
import { useFormatters } from '@/i18n/format';
import { ApiError } from '@/lib/api-client';
import type { OrgRole } from '@app/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, createFileRoute } from '@tanstack/react-router';
import { Copy, Loader2, Mail, Shield, Trash2, UserPlus, Users } from 'lucide-react';
import { type FormEvent, type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/app/settings/members')({
  component: MembersPage,
});

const ROLE_OPTIONS: OrgRole[] = ['owner', 'admin', 'member'];
const SELECT_CLASSES =
  'h-9 w-full rounded-md border bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

function emailInitials(email: string): string {
  const local = email.split('@')[0] ?? '';
  if (local.length === 0) return '?';
  const parts = local.split(/[._-]/u).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

function MembersPage(): ReactElement | null {
  const session = useSession();
  if (!session) return null;

  const role = session.activeOrg.role;
  if (role === 'member') {
    return <Navigate to="/app" replace />;
  }

  return <MembersPageContent canManageRoles={role === 'owner'} />;
}

function MembersPageContent({ canManageRoles }: { canManageRoles: boolean }): ReactElement {
  const { t } = useTranslation();
  const session = useSession();
  const orgId = session?.activeOrg.id ?? '';
  const currentUserId = session?.user.id ?? '';
  const queryClient = useQueryClient();

  const members = useQuery({
    queryKey: ['org-members', orgId],
    queryFn: () => listMembers(orgId),
    enabled: orgId.length > 0,
  });

  const invites = useQuery({
    queryKey: ['org-invites', orgId],
    queryFn: () => listInvites(orgId),
    enabled: orgId.length > 0,
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [createdInvite, setCreatedInvite] = useState<CreateInviteResult | null>(null);

  const memberRows = members.data?.members ?? [];
  const ownerCount = memberRows.filter((m) => m.role === 'owner').length;

  return (
    <div className="max-w-4xl space-y-8">
      <PageHeader
        eyebrow={t('app.layout.section.settings', { defaultValue: 'Settings' })}
        title={t('settings.members.title')}
        description={t('settings.members.description')}
        actions={
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="size-4" aria-hidden />
            {t('settings.members.inviteButton')}
          </Button>
        }
      />

      {createdInvite ? (
        <Alert>
          <AlertTitle>{t('settings.members.inviteCreatedTitle')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>
              {t('settings.members.inviteCreatedBody', {
                email: createdInvite.invite.email,
                role: t(`settings.members.role.${createdInvite.invite.role}`),
              })}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md border bg-muted px-2 py-1 font-mono text-xs">
                {createdInvite.acceptUrl}
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={async () => {
                  await navigator.clipboard.writeText(createdInvite.acceptUrl);
                  toast.success(t('settings.members.copySuccess'));
                }}
              >
                <Copy className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setCreatedInvite(null)}
              >
                {t('settings.members.hide')}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t('settings.members.membersHeader')}
        </h2>
        {members.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
          </div>
        ) : memberRows.length === 0 ? (
          <EmptyState icon={Users} title={t('settings.members.empty')} />
        ) : (
          <ul className="space-y-2">
            {memberRows.map((m) => (
              <li key={m.userId}>
                <MemberRow
                  member={m}
                  orgId={orgId}
                  isSelf={m.userId === currentUserId}
                  canManageRoles={canManageRoles}
                  ownerCount={ownerCount}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t('settings.members.invitesHeader')}
        </h2>
        {invites.isLoading ? (
          <Skeleton className="h-20 rounded-xl" />
        ) : (invites.data?.invites ?? []).length === 0 ? (
          <EmptyState icon={Mail} title={t('settings.members.invitesEmpty')} />
        ) : (
          <ul className="space-y-2">
            {invites.data?.invites.map((inv) => (
              <li key={inv.id}>
                <InviteRow invite={inv} orgId={orgId} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        orgId={orgId}
        onSuccess={(result) => {
          setCreatedInvite(result);
          setInviteOpen(false);
          queryClient.invalidateQueries({ queryKey: ['org-invites', orgId] });
        }}
      />
    </div>
  );
}

interface MemberRowProps {
  member: MemberSummary;
  orgId: string;
  isSelf: boolean;
  canManageRoles: boolean;
  ownerCount: number;
}

function MemberRow({
  member,
  orgId,
  isSelf,
  canManageRoles,
  ownerCount,
}: MemberRowProps): ReactElement {
  const { t } = useTranslation();
  const { date } = useFormatters();
  const queryClient = useQueryClient();
  const [roleOpen, setRoleOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  const isLastOwner = member.role === 'owner' && ownerCount <= 1;

  const remove = useMutation({
    mutationFn: () => removeMember(orgId, member.userId),
    onSuccess: () => {
      toast.success(t('settings.members.removeSuccess'));
      queryClient.invalidateQueries({ queryKey: ['org-members', orgId] });
      setRemoveOpen(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : t('settings.members.removeFailed'));
    },
  });

  return (
    <Card className="border-border/70 shadow-soft">
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <Avatar className="h-10 w-10">
          <AvatarFallback>{emailInitials(member.email)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-1">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <span>{member.email}</span>
            {isSelf ? <Badge variant="secondary">{t('settings.members.youBadge')}</Badge> : null}
            <RoleBadge role={member.role} />
            {member.disabledAt ? (
              <Badge variant="destructive">{t('settings.members.disabledBadge')}</Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            {t('settings.members.joinedAt', { when: date(member.createdAt) })}
          </CardDescription>
        </div>
        <div className="flex flex-col gap-1.5">
          {canManageRoles && !isSelf ? (
            <Button variant="outline" size="sm" onClick={() => setRoleOpen(true)}>
              <Shield className="size-3.5" aria-hidden />
              {t('settings.members.changeRole')}
            </Button>
          ) : null}
          {!isSelf ? (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setRemoveOpen(true)}
              disabled={isLastOwner}
              title={isLastOwner ? t('settings.members.lastOwnerHint') : undefined}
            >
              <Trash2 className="size-3.5" aria-hidden />
              {t('settings.members.remove')}
            </Button>
          ) : null}
        </div>
      </CardHeader>

      {canManageRoles ? (
        <ChangeRoleDialog
          member={member}
          orgId={orgId}
          open={roleOpen}
          onOpenChange={setRoleOpen}
          isLastOwner={isLastOwner}
        />
      ) : null}

      <ConfirmRemoveDialog
        member={member}
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        onConfirm={() => remove.mutate()}
        pending={remove.isPending}
      />
    </Card>
  );
}

function RoleBadge({ role }: { role: OrgRole }): ReactElement {
  const { t } = useTranslation();
  const variant: 'default' | 'secondary' | 'outline' =
    role === 'owner' ? 'default' : role === 'admin' ? 'secondary' : 'outline';
  return <Badge variant={variant}>{t(`settings.members.role.${role}`)}</Badge>;
}

interface ChangeRoleDialogProps {
  member: MemberSummary;
  orgId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  isLastOwner: boolean;
}

function ChangeRoleDialog({
  member,
  orgId,
  open,
  onOpenChange,
  isLastOwner,
}: ChangeRoleDialogProps): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [role, setRole] = useState<OrgRole>(member.role);

  const update = useMutation({
    mutationFn: (next: OrgRole) => updateMemberRole(orgId, member.userId, next),
    onSuccess: () => {
      toast.success(t('settings.members.roleChangeSuccess'));
      queryClient.invalidateQueries({ queryKey: ['org-members', orgId] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : t('settings.members.roleChangeFailed'));
    },
  });

  function handleOpenChange(next: boolean): void {
    if (!next) setRole(member.role);
    onOpenChange(next);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (role === member.role) return;
    update.mutate(role);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('settings.members.changeRoleTitle')}</DialogTitle>
            <DialogDescription>
              {t('settings.members.changeRoleDescription', { email: member.email })}
            </DialogDescription>
          </DialogHeader>

          {isLastOwner && member.role === 'owner' ? (
            <Alert variant="destructive">
              <AlertDescription>{t('settings.members.lastOwnerHint')}</AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor={`role-${member.userId}`}>{t('settings.members.roleLabel')}</Label>
            <select
              id={`role-${member.userId}`}
              className={SELECT_CLASSES}
              value={role}
              onChange={(e) => setRole(e.target.value as OrgRole)}
              disabled={update.isPending}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {t(`settings.members.role.${r}`)}
                </option>
              ))}
            </select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={update.isPending}
            >
              {t('settings.members.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={
                role === member.role ||
                update.isPending ||
                (isLastOwner && member.role === 'owner' && role !== 'owner')
              }
            >
              {update.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              {t('settings.members.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ConfirmRemoveDialogProps {
  member: MemberSummary;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
}

function ConfirmRemoveDialog({
  member,
  open,
  onOpenChange,
  onConfirm,
  pending,
}: ConfirmRemoveDialogProps): ReactElement {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('settings.members.removeTitle')}</DialogTitle>
          <DialogDescription>
            {t('settings.members.removeDescription', { email: member.email })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t('settings.members.cancel')}
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={pending}>
            {pending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {t('settings.members.remove')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface InviteRowProps {
  invite: InviteSummary;
  orgId: string;
}

function InviteRow({ invite, orgId }: InviteRowProps): ReactElement {
  const { t } = useTranslation();
  const { date } = useFormatters();
  const queryClient = useQueryClient();

  const revoke = useMutation({
    mutationFn: () => revokeInvite(orgId, invite.id),
    onSuccess: () => {
      toast.success(t('settings.members.revokeSuccess'));
      queryClient.invalidateQueries({ queryKey: ['org-invites', orgId] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : t('settings.members.revokeFailed'));
    },
  });

  return (
    <Card className="border-border/70 shadow-soft">
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Mail className="size-4" aria-hidden />
        </span>
        <div className="flex-1 space-y-1">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <span>{invite.email}</span>
            <RoleBadge role={invite.role} />
          </CardTitle>
          <CardDescription>
            {t('settings.members.inviteSentAt', { when: date(invite.createdAt) })} ·{' '}
            {t('settings.members.inviteExpiresAt', { when: date(invite.expiresAt) })}
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={() => revoke.mutate()}
          disabled={revoke.isPending}
        >
          {revoke.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Trash2 className="size-3.5" aria-hidden />
          )}
          {t('settings.members.revoke')}
        </Button>
      </CardHeader>
    </Card>
  );
}

interface InviteDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  orgId: string;
  onSuccess: (result: CreateInviteResult) => void;
}

function InviteDialog({ open, onOpenChange, orgId, onSuccess }: InviteDialogProps): ReactElement {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createInvite(orgId, { email: email.trim(), role }),
    onSuccess: (result) => {
      toast.success(t('settings.members.inviteSuccess'));
      setEmail('');
      setRole('member');
      setError(null);
      onSuccess(result);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('settings.members.inviteFailed'));
    },
  });

  function handleOpenChange(next: boolean): void {
    if (!next) {
      setEmail('');
      setRole('member');
      setError(null);
    }
    onOpenChange(next);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (!email.trim()) return;
    create.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('settings.members.inviteTitle')}</DialogTitle>
            <DialogDescription>{t('settings.members.inviteDescription')}</DialogDescription>
          </DialogHeader>

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="invite-email">{t('settings.members.emailLabel')}</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('settings.members.emailPlaceholder')}
              autoFocus
              disabled={create.isPending}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-role">{t('settings.members.roleLabel')}</Label>
            <select
              id="invite-role"
              className={SELECT_CLASSES}
              value={role}
              onChange={(e) => setRole(e.target.value as OrgRole)}
              disabled={create.isPending}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {t(`settings.members.role.${r}`)}
                </option>
              ))}
            </select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={create.isPending}
            >
              {t('settings.members.cancel')}
            </Button>
            <Button type="submit" disabled={!email.trim() || create.isPending}>
              {create.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              {t('settings.members.sendInvite')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
